/**
 * §E · Market & Customer — mechanically extracted from BrandBiblePage.
 *
 * AI Suggest uses the shared structured contract (section E, drafts/refusals,
 * explicit Accept). Competitive Set is §F and is not mounted here.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, RefreshCw, Sparkles, X } from 'lucide-react';
import { PersonaCard } from '../index';
import { FieldLabel, TextArea } from '../formPrimitives';
import { SectionHeader } from '../SectionHeader';
import type { BrandBibleDraft } from '../../../pages/brand-bible/draftModel';
import {
  useBbDraftCommit,
  useRegisterBbSectionSave,
  type BbSectionSaveRegistration,
  type DirtyFormSaveResult,
} from '@/components/brand-bible/save';
import {
  requestModelFieldSuggestions,
  type FetchFieldSuggestions,
  type FieldDraft,
} from '@/services/brandBibleAiSuggest';
import { useTenantStore } from '@/stores/useTenantStore';
import { selectEntries, useBrandBibleWizardStore } from '@/stores/brandBibleWizardStore';
import { useSingleton } from '@/components/brand-bible-wizard/steps/useSingleton';
import type { BuyerPersona } from '@/types/tenant';
import {
  applyPersonaSuggestion,
  allowedFieldsForTargets,
  buildAudienceRequest,
  contextIsEmpty,
  EMPTY_CONTEXT_REFUSAL,
  localRefusalResult,
  mapSuggestResponse,
  MISSING_TENANT_REFUSAL,
  parsePersonaTargetField,
  personaDraftLabel,
  type MappedSuggestResult,
  type PersonaSuggestionTarget,
} from './sectionAudienceAiSuggest';

const PERSONA_COLORS = ['#4CC9F0', '#0A76A8', '#111111', '#FBBF24', '#16A34A', '#6B7280', '#7C3AED', '#EC4899'];

const DISMISS_KEY = 'bb-ai-dismissed-v1';

function readDismissed(sectionId: string): string[] {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return [];
    const all = JSON.parse(raw) as Record<string, string[]>;
    return all[sectionId] || [];
  } catch {
    return [];
  }
}

function writeDismissed(sectionId: string, list: string[]) {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, string[]>) : {};
    all[sectionId] = list;
    localStorage.setItem(DISMISS_KEY, JSON.stringify(all));
  } catch {
    // ignore quota / privacy mode
  }
}

export {
  applyPersonaSuggestion,
  SECTION_E_EDITABLE_FIELDS,
  SECTION_E_EXPLICIT_REFUSALS,
  SECTION_E_SUGGESTION_GROUPS,
} from './sectionAudienceAiSuggest';

export interface DemographicProvenance {
  personaIndex: number;
  value: string;
  verified: false;
  source: string;
}

export interface SectionAudienceProps {
  sectionRef: (el: HTMLElement | null) => void;
  draft: BrandBibleDraft;
  set: <K extends keyof BrandBibleDraft>(key: K, value: BrandBibleDraft[K]) => void;
  addPersona: () => void;
  setPersonas: (next: BuyerPersona[]) => void;
  /** Bodies of §E `icp_note` market-note chunks. */
  icpNotes?: string[];
  /** Bodies of §E `seasonality_note` market-note chunks. */
  seasonalityNotes?: string[];
  /** Persist an accepted market-note draft. Page may wire this; tests inject it. */
  onAcceptMarketNote?: (kind: 'icp_note' | 'seasonality_note', value: string) => void;
  currentTenant?: string | null;
  fetchFieldSuggestions?: FetchFieldSuggestions;
}

type GroupState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; result: MappedSuggestResult };

/**
 * §E explicit-save buffer — the three CONVERTED (LEGACY_ONLY_SCALAR) columns
 * §E owns: customer_pain_points, website_audience, buyer_personas. Once §E is
 * in CONVERTED_SECTIONS the page autosave strips these three, so they buffer
 * locally and the section's Save button (via the page commit seam) is their
 * only write path. ideal_client and seasonality are deliberately NOT buffered
 * — they are `context`-class (bb_chunks homes) and 422 on the flat path; their
 * controls keep calling the page `set(...)`.
 */
type AudienceBuffer = {
  customer_pain_points: string;
  website_audience: string;
  buyer_personas: BuyerPersona[];
};

const bufferFromDraft = (d: BrandBibleDraft): AudienceBuffer => ({
  customer_pain_points: d.customer_pain_points ?? '',
  website_audience: d.website_audience ?? '',
  buyer_personas: d.buyer_personas ?? [],
});

/** Reference/value compare for the three buffered fields (personas by ref). */
const draftFieldsEqual = (a: AudienceBuffer, d: BrandBibleDraft): boolean =>
  a.customer_pain_points === (d.customer_pain_points ?? '') &&
  a.website_audience === (d.website_audience ?? '') &&
  a.buyer_personas === (d.buyer_personas ?? []);

function personasEqual(a: BuyerPersona, b: BuyerPersona): boolean {
  if (a === b) return true;
  return (
    a.segment_name === b.segment_name &&
    (a.demographics ?? '') === (b.demographics ?? '') &&
    JSON.stringify(a.pain_points ?? []) === JSON.stringify(b.pain_points ?? []) &&
    JSON.stringify(a.objections ?? []) === JSON.stringify(b.objections ?? [])
  );
}

/**
 * Persona diff is index-aligned: each slot whose contents differ from the
 * baseline counts once, and the length delta (personas added or removed) counts
 * once each. So an in-place edit = 1, an append = 1, a trailing delete = 1.
 * Personas carry no stable id, so a *mid-list* removal re-indexes the tail and
 * may count higher than one — an index diff is the honest best effort, and the
 * common actions (append, in-place edit, trailing delete) are exact.
 */
function personaChangeCount(base: BuyerPersona[], buf: BuyerPersona[]): number {
  const overlap = Math.min(base.length, buf.length);
  let changed = Math.abs(base.length - buf.length);
  for (let i = 0; i < overlap; i += 1) {
    if (!personasEqual(base[i], buf[i])) changed += 1;
  }
  return changed;
}

/** Real per-field diff vs the clean baseline. Three top-level fields, with
 *  buyer_personas expanded to one change per persona touched. */
function audienceChangeCount(base: AudienceBuffer, buf: AudienceBuffer): number {
  let n = 0;
  if (base.customer_pain_points !== buf.customer_pain_points) n += 1;
  if (base.website_audience !== buf.website_audience) n += 1;
  n += personaChangeCount(base.buyer_personas, buf.buyer_personas);
  return n;
}

export function SectionAudience({
  sectionRef,
  draft,
  set,
  addPersona,
  setPersonas,
  icpNotes,
  seasonalityNotes,
  onAcceptMarketNote,
  currentTenant,
  fetchFieldSuggestions,
}: SectionAudienceProps) {
  const storeTenant = useTenantStore((s) => s.currentTenant);
  const tenantId = currentTenant ?? storeTenant;
  // §E ICP silent-discard gate. brand_bible.ideal_client is class `context`
  // (its typed home is a bb_chunks icp_note row); it is excluded from
  // LEGACY_ONLY_SCALAR_KEYS and from pushDraftToV2, so for a v2-active tenant the
  // legacy flat-row write path never persists it — the free-text editor AND an
  // accepted `ideal_client` AI suggestion are both silently discarded (see
  // BrandBiblePage's "remain silently unsaved for v2-active tenants" note and
  // bbLegacyFieldOwnership's "Deliberately ABSENT" comment). isV2Active mirrors
  // the page's own predicate (Boolean(identity.entry)); when true we hide the
  // dead editor and drop ideal_client from the AI-Suggest field set so profile
  // suggestions route to icp_note, which has a working Accept path via
  // onAcceptMarketNote → the "Market notes" list rendered by the page. A v1
  // (non-v2) tenant is unaffected. Gating idiom mirrors §D's PR #1623.
  const isV2Active = Boolean(useSingleton('identity', useBrandBibleWizardStore((s) => s.tenantId) ?? '').entry);
  const chunkEntries = useBrandBibleWizardStore(selectEntries('chunks'));
  const notesOf = (kind: 'icp_note' | 'seasonality_note') =>
    chunkEntries
      .filter((e) => e.kind === kind && e.deleted_at == null)
      .map((e) => (e.body ?? '').trim())
      .filter(Boolean);
  const resolvedIcpNotes = icpNotes ?? notesOf('icp_note');
  const resolvedSeasonalityNotes = seasonalityNotes ?? notesOf('seasonality_note');
  const fetchSuggestions: FetchFieldSuggestions =
    fetchFieldSuggestions ?? ((request) => requestModelFieldSuggestions(request, tenantId));

  // ── §E explicit-save buffer ────────────────────────────────────────
  // The three convertible columns edit into `buffer`, not the page draft, so
  // the page draft stays the clean baseline that discard() restores from and
  // that the (now §E-stripped) autosave leaves alone. `baseline` only advances
  // on a successful save() or an external rehydrate — never on the page's
  // optimistic setDraft — so a failed save stays dirty instead of silently
  // going clean.
  const commit = useBbDraftCommit();
  const [baseline, setBaseline] = useState<AudienceBuffer>(() => bufferFromDraft(draft));
  const [buffer, setBuffer] = useState<AudienceBuffer>(() => bufferFromDraft(draft));
  // Refs mirror the state so the registration's isDirty()/changeCount() report
  // the post-save/-discard truth SYNCHRONOUSLY. The registry checks isDirty()
  // the instant save() resolves — before React re-renders and re-registers —
  // to fire the dirty→clean completion moment, so a closed-over state value
  // (stale until re-render) would make it miss the crossing. save() advances
  // baselineRef and discard() rewinds bufferRef right away; the setState calls
  // then keep the rendered controls in step.
  const bufferRef = useRef(buffer);
  bufferRef.current = buffer;
  const baselineRef = useRef(baseline);
  baselineRef.current = baseline;

  const [painState, setPainState] = useState<GroupState>({ status: 'idle' });
  const [profileState, setProfileState] = useState<GroupState>({ status: 'idle' });
  const [personaState, setPersonaState] = useState<GroupState>({ status: 'idle' });
  const [demoProvenance, setDemoProvenance] = useState<DemographicProvenance[]>([]);

  // Draft field-identities this section has reconciled with. save() stamps the
  // committed identities here BEFORE the page's optimistic setDraft lands, so
  // the sync effect below does not mistake a self-commit for a rehydrate and
  // wipe the just-saved buffer. Only a genuine external replacement (tenant
  // switch / rehydrate) makes draft diverge from this.
  const syncedRef = useRef<AudienceBuffer>(bufferFromDraft(draft));
  useEffect(() => {
    if (draftFieldsEqual(syncedRef.current, draft)) return;
    const fresh = bufferFromDraft(draft);
    syncedRef.current = fresh;
    setBaseline(fresh);
    setBuffer(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.customer_pain_points, draft.website_audience, draft.buyer_personas]);

  const registration = useMemo<BbSectionSaveRegistration>(
    () => ({
      isDirty: () => audienceChangeCount(baselineRef.current, bufferRef.current) > 0,
      changeCount: () => audienceChangeCount(baselineRef.current, bufferRef.current),
      save: async (): Promise<DirtyFormSaveResult> => {
        if (!commit) {
          return {
            success: false,
            error:
              'Cannot save Market & Customer — the Brand Bible page did not provide a save path. Reload and try again.',
          };
        }
        const snapshot = bufferRef.current;
        // Acknowledge the draft state our commit is about to produce (the page
        // merges these three columns into the draft) so the sync effect does
        // not revert the buffer when that optimistic setDraft re-renders us.
        syncedRef.current = snapshot;
        const ok = await commit(
          {
            customer_pain_points: snapshot.customer_pain_points,
            website_audience: snapshot.website_audience,
            buyer_personas: snapshot.buyer_personas,
          },
          'E',
        );
        if (!ok) {
          return {
            success: false,
            error: 'Market & Customer could not be saved — your edits are still here. Try again.',
          };
        }
        // Advance the baseline synchronously so isDirty() reads clean the moment
        // the registry checks it (the completion-moment crossing); setBaseline
        // keeps the render state in step.
        baselineRef.current = snapshot;
        setBaseline(snapshot);
        return true;
      },
      discard: () => {
        // Rewind the buffer synchronously (isDirty() clean before the registry's
        // post-discard isAnyDirty check); setBuffer keeps the controls in step.
        bufferRef.current = baselineRef.current;
        setBuffer(baselineRef.current);
        setDemoProvenance([]);
      },
    }),
    // baseline/buffer are here to give the registration a FRESH identity on
    // every edit — that re-registration is what notifies the footer's reactive
    // changeCount/dirty hooks. The method bodies deliberately read the refs, not
    // these values, so isDirty() is synchronously correct after save/discard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseline, buffer, commit],
  );
  useRegisterBbSectionSave('E', registration);

  const input = {
    businessName: draft.business_name,
    idealClient: draft.ideal_client,
    customerPainPoints: buffer.customer_pain_points,
    websiteAudience: buffer.website_audience,
    icpNotes: resolvedIcpNotes,
    seasonalityNotes: resolvedSeasonalityNotes,
    personas: buffer.buyer_personas,
  };

  const runGroup = async (
    groupId: 'pain-points' | 'profile-notes' | 'persona-details',
    setState: (s: GroupState) => void,
    sectionId: string,
  ) => {
    setState({ status: 'loading' });
    const dismissed = readDismissed(sectionId);
    const request = buildAudienceRequest(groupId, input, dismissed);
    const allowed = allowedFieldsForTargets(request.targets);

    if (!tenantId) {
      setState({ status: 'loaded', result: localRefusalResult(request.targets, MISSING_TENANT_REFUSAL) });
      return;
    }
    if (contextIsEmpty(request)) {
      setState({ status: 'loaded', result: localRefusalResult(request.targets, EMPTY_CONTEXT_REFUSAL) });
      return;
    }

    try {
      const response = await fetchSuggestions(request);
      setState({ status: 'loaded', result: mapSuggestResponse(response, allowed) });
    } catch {
      setState({ status: 'error' });
    }
  };

  const acceptPainPoint = (value: string) => {
    setBuffer((b) => ({
      ...b,
      customer_pain_points: (b.customer_pain_points ? b.customer_pain_points + ', ' : '') + value,
    }));
  };

  const acceptPersonaDraft = (draftItem: FieldDraft, source: string) => {
    const parsed =
      parsePersonaTargetField(draftItem.target_field) ?? parsePersonaTargetField(draftItem.output_key);
    if (!parsed) return;
    const bound: PersonaSuggestionTarget = { ...parsed, value: draftItem.value };
    const next = applyPersonaSuggestion(bufferRef.current.buyer_personas, bound);
    setBuffer((b) => ({ ...b, buyer_personas: next }));
    const personaIndex = parsed.personaIndex;
    if (parsed.field === 'demographics' && personaIndex !== 'new') {
      setDemoProvenance((prev) => [
        ...prev.filter((p) => p.personaIndex !== personaIndex),
        {
          personaIndex,
          value: draftItem.value,
          verified: false,
          source,
        },
      ]);
    }
  };

  // Buffer-local mutations for the three converted fields (they must not hit
  // the page `set`/`setPersonas`/`addPersona`, which write straight to the
  // draft). ideal_client stays on `set`.
  const addPersonaToBuffer = () =>
    setBuffer((b) => ({
      ...b,
      buyer_personas: [
        ...b.buyer_personas,
        { segment_name: '', demographics: '', pain_points: [], objections: [] },
      ],
    }));
  const updatePersonaAt = (i: number, pp: BuyerPersona) =>
    setBuffer((b) => {
      const arr = [...b.buyer_personas];
      arr[i] = pp;
      return { ...b, buyer_personas: arr };
    });
  const removePersonaAt = (i: number) => {
    setBuffer((b) => {
      const arr = [...b.buyer_personas];
      arr.splice(i, 1);
      return { ...b, buyer_personas: arr };
    });
    setDemoProvenance((prev) =>
      prev
        .filter((row) => row.personaIndex !== i)
        .map((row) => (row.personaIndex > i ? { ...row, personaIndex: row.personaIndex - 1 } : row)),
    );
  };

  return (
    <section
      ref={(el) => {
        sectionRef(el);
      }}
      className="bg-card border border-border rounded-2xl px-6 py-5 mb-4 shadow-sm"
    >
      <SectionHeader
        id="audience"
        right={
          <div className="flex items-center gap-2">
            <SuggestControl
              placeholder="Suggest pain points"
              sectionId="audience"
              state={painState}
              onFetch={() => void runGroup('pain-points', setPainState, 'audience')}
              kind="chips"
              onAcceptDraft={(d) => acceptPainPoint(d.value)}
              fieldOrder={['customer_pain_points']}
            />
            <button
              type="button"
              onClick={addPersonaToBuffer}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-card border border-border text-foreground text-[12px] font-medium hover:bg-warm-bg cursor-pointer"
            >
              <Plus className="size-3.5" />
              Add persona
            </button>
          </div>
        }
      />
      <div className="grid grid-cols-2 gap-3.5 mb-4">
        <div>
          {/* Legacy free-text ICP editor — v1 ONLY. For a v2-active tenant this
              textarea writes into the dead brand_bible.ideal_client field (see
              isV2Active above): input is silently discarded. It is not lost for
              v2 tenants — the page's "Market notes — counted by the score"
              ChunkVerifyListBody below renders/verifies/edits the icp_note
              bodies, and the AI-Suggest control (kept, with ideal_client dropped
              from fieldOrder) routes accepted suggestions there. A v1 tenant
              keeps this editor exactly as before. Mirrors §D's PR #1623.
              v2 keeps the cell LABELLED (no `required` — it can't be filled
              directly) so the Suggest control below isn't an orphaned,
              contextless button, and points the operator at where accepted
              suggestions land. */}
          {isV2Active ? (
            <FieldLabel hint="suggestions save to Market notes below">Ideal client profile</FieldLabel>
          ) : (
            <>
              <FieldLabel required>Ideal client profile</FieldLabel>
              <TextArea
                value={draft.ideal_client}
                onChange={(v) => set('ideal_client', v)}
                rows={2}
              />
            </>
          )}
          <div className="mt-1.5">
            <SuggestControl
              placeholder="Suggest profile"
              sectionId="audience_profile"
              state={profileState}
              onFetch={() => void runGroup('profile-notes', setProfileState, 'audience_profile')}
              kind="text"
              fieldOrder={
                isV2Active
                  ? ['icp_note', 'seasonality_note']
                  : ['ideal_client', 'icp_note', 'seasonality_note']
              }
              onAcceptDraft={(d) => {
                if (d.target_field === 'ideal_client' || d.output_key === 'ideal_client') {
                  set('ideal_client', d.value);
                  return;
                }
                if (d.target_field === 'icp_note' || d.output_key === 'icp_note') {
                  onAcceptMarketNote?.('icp_note', d.value);
                  return;
                }
                if (d.target_field === 'seasonality_note' || d.output_key === 'seasonality_note') {
                  onAcceptMarketNote?.('seasonality_note', d.value);
                }
              }}
            />
          </div>
        </div>
        <div>
          <FieldLabel required>Top pain points</FieldLabel>
          <TextArea
            value={buffer.customer_pain_points}
            onChange={(v) => setBuffer((b) => ({ ...b, customer_pain_points: v }))}
            rows={2}
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">
          Buyer personas
        </div>
        <SuggestControl
          placeholder="Suggest persona details"
          sectionId="audience_personas"
          state={personaState}
          onFetch={() => void runGroup('persona-details', setPersonaState, 'audience_personas')}
          kind="chips"
          personas={buffer.buyer_personas}
          onAcceptDraft={(d, source) => acceptPersonaDraft(d, source)}
        />
      </div>
      <div className="grid grid-cols-2 gap-3.5">
        {buffer.buyer_personas.map((p, i) => (
          <div key={i}>
            <PersonaCard
              data={p}
              accent={PERSONA_COLORS[i % PERSONA_COLORS.length]}
              onChange={(pp) => updatePersonaAt(i, pp)}
              onRemove={() => removePersonaAt(i)}
            />
            {demoProvenance
              .filter((row) => row.personaIndex === i)
              .map((row) => (
                <div
                  key={`${row.personaIndex}-${row.value}`}
                  data-testid={`bb-e-demo-provenance-${i}`}
                  className="mt-1 text-[11px] text-muted-foreground"
                >
                  unverified · {row.source}
                </div>
              ))}
          </div>
        ))}
        <button
          type="button"
          onClick={addPersonaToBuffer}
          className="border border-dashed border-border bg-transparent rounded-xl min-h-[230px] text-muted-foreground text-[13px] cursor-pointer flex flex-col items-center justify-center gap-1.5 hover:border-coral hover:text-foreground transition-colors"
        >
          <span className="grid place-items-center w-8 h-8 rounded-full bg-warm-bg">
            <Plus className="size-4" />
          </span>
          Add another persona
        </button>
      </div>
    </section>
  );
}

function SuggestControl({
  placeholder,
  sectionId,
  state,
  onFetch,
  kind,
  onAcceptDraft,
  fieldOrder,
  personas = [],
}: {
  placeholder: string;
  sectionId: string;
  state: GroupState;
  onFetch: () => void;
  kind: 'chips' | 'text';
  onAcceptDraft: (draft: FieldDraft, source: string) => void;
  fieldOrder?: string[];
  personas?: BuyerPersona[];
}) {
  const [dismissed, setDismissed] = useState<string[]>(() => readDismissed(sectionId));

  const dismissValue = (value: string) => {
    setDismissed((prev) => {
      const next = prev.includes(value) ? prev : [...prev, value];
      writeDismissed(sectionId, next);
      return next;
    });
  };

  if (state.status === 'idle') {
    return (
      <button
        type="button"
        onClick={onFetch}
        className="animate-ai-suggest inline-flex items-center gap-1.5 pl-2.5 pr-2.5 py-1.5 rounded-md bg-teal-light text-teal-dark border border-teal/40 text-[12px] font-semibold cursor-pointer hover:bg-teal-light/80"
      >
        <Sparkles className="size-3.5" />
        {placeholder}
      </button>
    );
  }

  if (state.status === 'loading') {
    return (
      <div className="inline-flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-md bg-teal-light text-teal-dark border border-teal/40 text-[12px] font-semibold">
        <Sparkles className="size-3.5 animate-spin" />
        Thinking…
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <button
        type="button"
        onClick={onFetch}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[hsl(var(--status-error-bg))] text-[hsl(var(--status-error))] border border-[hsl(var(--status-error)/0.3)] text-[12px] font-semibold cursor-pointer"
      >
        Couldn&apos;t fetch · retry
      </button>
    );
  }

  const { result } = state;
  const visibleDrafts = result.drafts.filter((d) => !dismissed.includes(d.value));
  const isLocalRefusal = result.source.startsWith('refusal');

  const removeDraft = (draft: FieldDraft) => {
    // Local-only hide until refresh; parent replaces whole result on refetch.
    dismissValue(draft.value);
  };

  const fieldRows = fieldOrder ?? [];
  const refusalsByField = new Map(result.refusals.map((r) => [r.target_field, r]));

  if (kind === 'chips') {
    if (visibleDrafts.length === 0) {
      return (
        <div className="flex flex-col items-end gap-1">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-transparent text-muted-foreground border border-dashed border-border text-[11.5px]">
            <span>No new ideas</span>
            <span data-testid={`bb-e-source-${sectionId}`} className="text-muted-foreground">
              {result.source}
            </span>
            <button
              type="button"
              onClick={onFetch}
              className="flex bg-transparent border-none text-teal-dark cursor-pointer hover:text-teal"
            >
              <RefreshCw className="size-3" />
            </button>
          </div>
          {result.refusals.map((r) => (
            <div
              key={`${r.target_field}-${r.reason}`}
              data-testid={`bb-e-refusal-${r.target_field}`}
              className="max-w-[480px] text-right text-[10.5px] text-muted-foreground"
            >
              {r.target_field}: {r.reason}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-1.5 items-end">
        <div className="inline-flex items-center gap-2 text-[11px] text-teal-dark font-semibold">
          <Sparkles className="size-3" />
          {visibleDrafts.length} idea{visibleDrafts.length !== 1 ? 's' : ''} ·{' '}
          <span data-testid={`bb-e-source-${sectionId}`} className="text-muted-foreground font-medium">
            {result.source}
          </span>
          <button
            type="button"
            onClick={onFetch}
            title="Refresh"
            className="grid place-items-center w-5 h-5 rounded bg-transparent border-none text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <RefreshCw className="size-3" />
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 justify-end max-w-[480px]">
          {visibleDrafts.map((d, i) => {
            const label = personas.length || parsePersonaTargetField(d.target_field)
              ? personaDraftLabel(d, personas)
              : d.value;
            return (
              <span
                key={`${d.output_key}-${d.value}-${i}`}
                data-testid={`bb-e-draft-${d.output_key}`}
                className="inline-flex items-center bg-card border border-teal/30 rounded-md text-[12px] text-foreground overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => {
                    onAcceptDraft(d, result.source);
                    dismissValue(d.value);
                  }}
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 border-none bg-transparent cursor-pointer text-foreground text-[12px] font-medium"
                >
                  <Plus className="size-2.5 text-teal-dark" />
                  {label}
                </button>
                <button
                  type="button"
                  onClick={() => removeDraft(d)}
                  title="Don't suggest again"
                  className="grid place-items-center w-5 self-stretch border-none border-l border-l-border bg-transparent text-muted-foreground hover:text-foreground cursor-pointer"
                >
                  <X className="size-2.5" />
                </button>
              </span>
            );
          })}
        </div>
        {result.refusals.map((r) => (
          <div
            key={`${r.target_field}-${r.reason}`}
            data-testid={`bb-e-refusal-${r.target_field}`}
            className="max-w-[480px] text-right text-[10.5px] text-muted-foreground"
          >
            {r.target_field}: {r.reason}
          </div>
        ))}
      </div>
    );
  }

  // text kind — profile + market notes. Every targeted field is listed so
  // icp_note / seasonality_note always appear as a draft or a refusal.
  const rows = fieldRows.length
    ? fieldRows
    : Array.from(new Set([...visibleDrafts.map((d) => d.target_field), ...result.refusals.map((r) => r.target_field)]));

  return (
    <div className="w-full mt-2.5 bg-teal-light border border-teal/30 rounded-xl p-3">
      <div className="flex items-center gap-2 mb-2 text-[11.5px] text-teal-dark font-semibold">
        <Sparkles className="size-3" />
        Rewrite suggestions ·{' '}
        <span data-testid={`bb-e-source-${sectionId}`} className="text-muted-foreground font-medium">
          {result.source}
        </span>
        <button
          type="button"
          onClick={onFetch}
          title="Refresh"
          className="ml-auto grid place-items-center w-[22px] h-[22px] rounded bg-transparent border-none text-muted-foreground hover:text-foreground cursor-pointer"
        >
          <RefreshCw className="size-3" />
        </button>
      </div>
      <div className="flex flex-col gap-2">
        {rows.map((field) => {
          const drafts = visibleDrafts.filter((d) => d.target_field === field || d.output_key === field);
          const refusal = refusalsByField.get(field);
          return (
            <div key={field} data-testid={`bb-e-field-${field}`}>
              <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                {field}
              </div>
              {drafts.map((d, i) => (
                <div
                  key={`${d.output_key}-${i}`}
                  data-testid={`bb-e-draft-${d.output_key}`}
                  className="bg-card border border-teal/20 rounded-lg px-3 py-2.5 flex gap-2.5 items-start mb-1.5"
                >
                  <div className="flex-1 text-[13px] text-foreground leading-relaxed">{d.value}</div>
                  <button
                    type="button"
                    onClick={() => {
                      onAcceptDraft(d, result.source);
                      dismissValue(d.value);
                    }}
                    className="px-2.5 py-1 rounded-md text-[11.5px] font-semibold border text-white cursor-pointer bg-coral hover:bg-coral-hover border-coral"
                  >
                    {field === 'ideal_client' ? 'Use this' : 'Accept'}
                  </button>
                </div>
              ))}
              {drafts.length === 0 && (
                <div
                  data-testid={`bb-e-refusal-${field}`}
                  className="text-[12px] text-muted-foreground"
                >
                  {refusal?.reason ?? (isLocalRefusal ? result.source : 'No safe draft')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
